<template>
  <div class="relative h-full w-full">
    <div :id="cacheId" ref="container" class="editor-container h-full w-full"></div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'

import { MapCache } from 'client/library/map-cache'
import { documentKit, useMindMap } from 'compositions/mindmap'
import { isInIframe } from 'utils/detectDevice'
import { createAsyncQueue, debounce } from 'utils/misc'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { useMasterLoading } from 'app/composables/master-loading'
import type { CloudStorageFile, EditorNextFile } from 'client/types/file'
import { WorkbookManager } from 'client/library/workbook-next/workbook-manager'
import { MapClickEventPlugin } from './plugins/embed-map-click-plugin'
import { useServiceStore } from 'client/store'
import { useAnimationFrameThrottle } from 'client/composables/animation-frame-throttle'
import { useResizeObserver } from '@vueuse/core'
import { useEmbedEditorShortcuts } from './composables/use-embed-editor-shortcuts'
import { useGlobalConfig } from '../composables/use-global-config'

const props = withDefaults(
  defineProps<{
    cacheId: string
    file: EditorNextFile | ArrayBuffer
    fileName?: string
    newDoc?: documentKit.Document
  }>(),
  {
    newDoc: null,
    fileName: 'Untitled.xmind',
  },
)

const { showMasterLoading, hideMasterLoading } = useMasterLoading()
showMasterLoading({ isShowCancelBtn: false })

const container = ref<HTMLElement | null>(null)

const { editor: editorState, workbook } = storeToRefs(useWorkspaceStore())
const isLoading = computed(() => editorState.value?.mapStatus !== 'loaded')
useEmbedEditorShortcuts()

const loadNewFile = async (file: EditorNextFile | ArrayBuffer, container: HTMLElement) => {
  const cacheId = props.cacheId
  const mapCache = MapCache.use(cacheId)
  const meta: CloudStorageFile = {
    id: cacheId,
    type: 'file',
    name: props.fileName,
    isTemporary: true,
  }
  await mapCache.update({
    meta,
    file,
  })

  useWorkspaceStore().createNewEditorState(meta, cacheId)
  useWorkspaceStore().setMapStatus('opening', cacheId)
  useWorkspaceStore().createNewEditorState(meta, cacheId)
  useWorkspaceStore().setMapStatus('opening', cacheId)

  try {
    await WorkbookManager.createWorkbookEditor({
      cacheId,
      file,
      container,
      xapGetter: mapCache.getXAP.bind(mapCache),
      xapSetter: mapCache.setXAP.bind(mapCache),
    })
  } catch (e) {
    useWorkspaceStore().setMapStatus('unloaded', cacheId)
    useWorkspaceStore().removeEditorState(cacheId)
    // TODO: treat open file error
    useMasterLoading().hideMasterLoading()
    logger.error('Failed to load file in embed editor', e)
  }

  useWorkspaceStore().setMapStatus('loaded', cacheId)
  useWorkspaceStore().setActivatedWorkbook(cacheId, WorkbookManager.find(cacheId))
}

const reloadMap = async () => {
  if (!props.cacheId || (editorState.value && editorState.value.cacheId === props.cacheId)) return
  await new Promise(resolve => setTimeout(resolve))
  if (!container.value) return

  let sdContainer = container.value.firstElementChild as HTMLElement
  if (!sdContainer) {
    sdContainer = document.createElement('div')
    sdContainer.setAttribute('class', 'w-full h-full')
    container.value.append(sdContainer)
  }

  await loadNewFile(props.file, sdContainer)
}

const queue = createAsyncQueue()

// map click event plugin
MapClickEventPlugin({ action: useServiceStore().services.action })

const debouncedUpdateEditorRect = debounce(() => {
  const domRect = container.value?.getBoundingClientRect?.()
  domRect &&
    useWorkspaceStore().setEditorRect(
      {
        x: domRect.x,
        y: domRect.y,
        width: domRect.width,
        height: domRect.height,
      },
      props.cacheId,
    )
}, 50)

const { throttle: animationThrottle } = useAnimationFrameThrottle()
// update viewport size when editor container is resized
const throttleCheckSize = () =>
  animationThrottle(() => {
    workbook.value?.workbookEditor?.activeSheetViewer.value?.checkSize?.()
  }, 1)

useResizeObserver(container, entries => {
  if (!entries || entries.length === 0) return
  throttleCheckSize()
  debouncedUpdateEditorRect()
})

onMounted(() => {
  queue(() => reloadMap())
})

onUnmounted(() => {
  hideMasterLoading()
})

watch(
  () => props.cacheId,
  () => queue(() => reloadMap()),
)
watch(isLoading, () => {
  if (!isLoading.value) {
    hideMasterLoading()
  }
})

const { embedEditorPresetType } = storeToRefs(useGlobalConfig())
const isMakerWideScreen = embedEditorPresetType.value === 'maker' && window.innerWidth > 800
if (isInIframe() && Math.min(window.innerWidth, window.innerHeight) < 600 && !isMakerWideScreen) {
  const unwatch = watch(
    useMindMap().activeSheetFigure,
    sheetFigure =>
      sheetFigure &&
      setTimeout(() => {
        useMindMap().executeEditorAction({ name: 'mutateEditor:fitMap' })
        unwatch?.()
      }, 200),
  )
}
</script>

<style lang="less" scoped>
.editor-container {
  // Prevent map topic text been selected
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome and Opera */
  :deep(.sheet-viewer) {
    width: 100%;
    height: 100%;
    position: absolute;
    overflow: auto;
    z-index: -1;
    opacity: 0;
    outline: none;
    &.active {
      z-index: 0;
      opacity: 1;
    }
  }
}
</style>
